/**
 * calibrationCurve — pure math for agent confidence calibration (AGENT-CALIB-1).
 *
 * No I/O, no Firestore, no React. All inputs are arrays of
 *   { predicted: number ∈ [0,1], actual: 0 | 1 }
 * samples. Functions return plain objects suitable for storage or rendering.
 *
 * Reference: Platt (1999) "Probabilistic outputs for SVMs and comparisons to
 * regularized likelihood methods." Standard sigmoid-of-linear fit by maximum
 * binomial likelihood. We use Newton-Raphson on the scalar logistic regression
 * with one feature (predicted probability) and one intercept — the closed-form
 * gradient/Hessian are tractable without external dependencies.
 *
 * Reference: ECE per Naeini, Cooper, Hauskrecht (2015), "Obtaining Well
 * Calibrated Probabilities Using Bayesian Binning." Decile binning, weighted
 * average of |confidence − accuracy| per bin.
 */

export const DEFAULT_BIN_COUNT = 10;
export const DEFAULT_ECE_THRESHOLD = 0.10;
export const DEFAULT_BRIER_REGRESSION_THRESHOLD = 0.05;
export const DEFAULT_ADJUSTMENT_DELTA = 0.01;

/**
 * Bin samples into equal-width buckets over [0,1] and compute per-bin
 * mean predicted probability and observed accuracy.
 *
 * @param {Array<{predicted:number, actual:0|1}>} samples
 * @param {number} binCount
 * @returns {Array<{ bin:number, lower:number, upper:number, count:number, meanPredicted:number, meanActual:number }>}
 */
export function buildCurve(samples, binCount = DEFAULT_BIN_COUNT) {
  const bins = Array.from({ length: binCount }, (_, i) => ({
    bin: i,
    lower: i / binCount,
    upper: (i + 1) / binCount,
    count: 0,
    sumPredicted: 0,
    sumActual: 0,
  }));

  for (const s of samples) {
    if (s.predicted < 0 || s.predicted > 1) continue;
    if (s.actual !== 0 && s.actual !== 1) continue;
    let idx = Math.floor(s.predicted * binCount);
    if (idx >= binCount) idx = binCount - 1;
    bins[idx].count += 1;
    bins[idx].sumPredicted += s.predicted;
    bins[idx].sumActual += s.actual;
  }

  return bins.map(b => ({
    bin: b.bin,
    lower: b.lower,
    upper: b.upper,
    count: b.count,
    meanPredicted: b.count > 0 ? b.sumPredicted / b.count : (b.lower + b.upper) / 2,
    meanActual: b.count > 0 ? b.sumActual / b.count : null,
  }));
}

/**
 * Expected Calibration Error: sample-weighted average of
 * |meanPredicted − meanActual| over non-empty bins.
 *
 * Range: [0, 1]. Lower is better. 0 = perfect calibration.
 */
export function expectedCalibrationError(curve) {
  const totalCount = curve.reduce((a, b) => a + b.count, 0);
  if (totalCount === 0) return 0;
  let ece = 0;
  for (const b of curve) {
    if (b.count === 0 || b.meanActual === null) continue;
    ece += (b.count / totalCount) * Math.abs(b.meanPredicted - b.meanActual);
  }
  return ece;
}

/**
 * Brier score: mean squared error between predicted probability and actual
 * binary outcome. Range: [0, 1]. Lower is better.
 */
export function brierScore(samples) {
  if (samples.length === 0) return 0;
  let sse = 0;
  let n = 0;
  for (const s of samples) {
    if (s.actual !== 0 && s.actual !== 1) continue;
    if (s.predicted < 0 || s.predicted > 1) continue;
    sse += (s.predicted - s.actual) ** 2;
    n += 1;
  }
  return n > 0 ? sse / n : 0;
}

function sigmoid(z) {
  if (z >= 0) {
    const e = Math.exp(-z);
    return 1 / (1 + e);
  }
  const e = Math.exp(z);
  return e / (1 + e);
}

/**
 * Fit Platt scaling: find (a, b) maximizing binomial log-likelihood of
 *   p_calibrated(x) = sigmoid(a · x + b)
 * over samples. Newton-Raphson on the 2-parameter log-likelihood.
 *
 * Returns { method: 'platt', params: { a, b }, iterations, converged }.
 *
 * Implementation notes:
 * - Uses Platt's original target smoothing (t+ = (N+ + 1)/(N+ + 2), t- = 1/(N- + 2))
 *   to avoid log(0) on perfectly-separated data.
 * - Adds tiny ridge term (1e-6) to Hessian diagonal for numerical stability.
 */
export function fitPlatt(samples, opts = {}) {
  const maxIter = opts.maxIter || 50;
  const tol = opts.tol || 1e-6;

  const cleaned = samples.filter(s =>
    s.predicted >= 0 && s.predicted <= 1 &&
    (s.actual === 0 || s.actual === 1)
  );

  if (cleaned.length < 2) {
    return { method: 'platt', params: { a: 1, b: 0 }, iterations: 0, converged: false, reason: 'insufficient_samples' };
  }

  const nPos = cleaned.filter(s => s.actual === 1).length;
  const nNeg = cleaned.length - nPos;
  const tPos = (nPos + 1) / (nPos + 2);
  const tNeg = 1 / (nNeg + 2);

  // Targets: smoothed labels per Platt 1999
  const data = cleaned.map(s => ({
    x: s.predicted,
    t: s.actual === 1 ? tPos : tNeg,
  }));

  // Initial guess: a=1, b=0 → identity-ish; or use log-odds of base rate
  const baseRate = nPos / cleaned.length;
  let a = 1;
  let b = baseRate > 0 && baseRate < 1
    ? Math.log(baseRate / (1 - baseRate))
    : 0;

  let converged = false;
  let iter = 0;

  for (iter = 0; iter < maxIter; iter++) {
    let g0 = 0, g1 = 0;
    let h00 = 0, h01 = 0, h11 = 0;
    for (const d of data) {
      const z = a * d.x + b;
      const p = sigmoid(z);
      const w = p * (1 - p);
      const err = p - d.t;
      g0 += err * d.x;
      g1 += err;
      h00 += w * d.x * d.x;
      h01 += w * d.x;
      h11 += w;
    }
    // Ridge for stability
    h00 += 1e-6;
    h11 += 1e-6;
    const det = h00 * h11 - h01 * h01;
    if (Math.abs(det) < 1e-12) break;
    const da = (h11 * g0 - h01 * g1) / det;
    const db = (-h01 * g0 + h00 * g1) / det;
    a -= da;
    b -= db;
    if (Math.abs(da) < tol && Math.abs(db) < tol) {
      converged = true;
      iter += 1;
      break;
    }
  }

  return {
    method: 'platt',
    params: { a, b },
    iterations: iter,
    converged,
  };
}

/**
 * Apply a calibration function to a raw confidence value. Returns calibrated
 * value in [0, 1]. If the function is null/unrecognized, returns rawConfidence
 * unchanged (identity pass-through).
 */
export function applyCalibration(rawConfidence, calibrationFn) {
  if (rawConfidence == null || Number.isNaN(rawConfidence)) return rawConfidence;
  if (!calibrationFn || calibrationFn.method !== 'platt') return rawConfidence;
  const { a, b } = calibrationFn.params || {};
  if (typeof a !== 'number' || typeof b !== 'number') return rawConfidence;
  const calibrated = sigmoid(a * rawConfidence + b);
  // Clamp for paranoia — sigmoid output is always [0,1] but rounding can poke past
  if (calibrated < 0) return 0;
  if (calibrated > 1) return 1;
  return calibrated;
}

/**
 * Decide whether a new calibration fit is meaningfully different from the
 * prior one, used to gate the AGENT-CALIBRATION-ADJUSTED event.
 */
export function isMeaningfulAdjustment(prevFn, newFn, opts = {}) {
  const delta = opts.delta ?? DEFAULT_ADJUSTMENT_DELTA;
  if (!prevFn) return true;
  if (!newFn) return false;
  if (prevFn.method !== newFn.method) return true;
  const pa = prevFn.params?.a ?? 1;
  const pb = prevFn.params?.b ?? 0;
  const na = newFn.params?.a ?? 1;
  const nb = newFn.params?.b ?? 0;
  return Math.abs(na - pa) > delta * 5 || Math.abs(nb - pb) > delta * 5;
}

/**
 * Decide whether the current curve warrants a fresh fit.
 */
export function shouldRecompute({ ece, prevBrier, currentBrier, eceThreshold = DEFAULT_ECE_THRESHOLD, brierThreshold = DEFAULT_BRIER_REGRESSION_THRESHOLD }) {
  if (ece > eceThreshold) return { recompute: true, reason: 'ece_threshold' };
  if (prevBrier != null && currentBrier - prevBrier > brierThreshold) {
    return { recompute: true, reason: 'brier_regression' };
  }
  return { recompute: false, reason: null };
}
